import { useMemo } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { GridColDef } from '@mui/x-data-grid';
import { IconButton, Stack, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import Chip from '@mui/material/Chip';
import { RecurrenceListItem, ShiftOld, ShiftStatus } from '@libs/models/shifts/shift';
import { AvatarList, ExternalIdLabel } from '@molecules/dataDisplay';
import { ShiftStatusChip, TooltipNote } from '@atoms/dataDisplay';
import { Box } from '@atoms/layout';
import { ShiftAssigneeButton } from '@pages/schedule/components';
import { RecurrenceDates } from './recurrenceDates';
import { FaIcon } from '@atoms/icons';
import { getUniqueObjects } from '@libs/utils';

export function useRecurrencesListColumns() {
  const theme = useTheme();
  const intl = useIntl();

  // Columns should be memoized to prevent table flickering and state loss
  return useMemo(() => {
    const columns: GridColDef<RecurrenceListItem>[] = [
      getRotationDatesColumn(intl),
      getExternalIdColumn(intl, 'shifts.listPage'),
      getAssignmentTypeColumn(intl),
      getQualificationsColumn(intl),
      getDepartmentColumn(intl),
      getSpecialtyColumn(intl),
      // getStatusColumn(intl),
      getTagsColumn(intl, theme),
      getNotesColumn(intl),
      getShareColumn(intl),
      getAssignedCountColumn(intl),
      getAssigneeColumn(intl),
    ];
    return columns;
  }, [intl]);
}

function getExternalIdColumn(intl: IntlShape, localizationSubset: string): GridColDef<RecurrenceListItem, string> {
  return {
    field: 'externalId',
    headerName: intl.formatMessage({
      id: `${localizationSubset}.table.fields.externalId.heading`,
    }),
    width: 10,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      return (
        <ExternalIdLabel
          localizationSubset={`${localizationSubset}.table.fields.externalId`}
          externalId={params.value}
          mode={'hoverView'}
        />
      );
    },
  };
}

function getAssignmentTypeColumn(
  intl: IntlShape,
): GridColDef<RecurrenceListItem, RecurrenceListItem['allowedMultipleAssignees']> {
  return {
    field: 'allowedMultipleAssignees',
    headerName: intl.formatMessage({
      id: 'shifts.fields.assignmentType',
    }),
    width: 170,
    renderCell: (params) => {
      const row = params.row;
      if (row.allowedMultipleAssignees === null) {
        // No information about assignment type
        return null;
      }
      if (row.allowedMultipleAssignees) {
        return <Chip variant="outlined" label={'Multiple Assignee'} size="small" />;
      }
      return <Chip variant="outlined" label={'Single Assignee'} size="small" />;
    },
  };
}

function getRotationDatesColumn(intl: IntlShape): GridColDef<RecurrenceListItem, RecurrenceListItem['startDate']> {
  return {
    field: 'startDate',
    headerName: intl.formatMessage({
      id: 'shifts.fields.dates',
    }),
    sortable: true,
    sortComparator: (v1, v2) => {
      if (!v1) {
        return 0;
      }
      if (!v2) {
        return 1;
      }
      return `${v1.toISOString()}`.localeCompare(`${v1.toISOString()}`);
    },
    width: 160,
    renderCell: (params) => {
      if (!params.row) {
        return null;
      }
      const row = params.row;
      return (
        <RecurrenceDates
          startDate={row.startDate}
          endDate={row.endDate}
          isShared={row.isShared}
          isSeries={row.recurrence === 'series'}
          isRotation={row.recurrence === 'rotation'}
          isSingle={row.allowedMultipleAssignees === false}
          isMultiple={!!row.allowedMultipleAssignees}
          isArchive={row.isArchive}
        />
      );
    },
  };
}

function getQualificationsColumn(
  intl: IntlShape,
): GridColDef<RecurrenceListItem, RecurrenceListItem['qualifications']> {
  return {
    field: 'qualifications',
    headerName: intl.formatMessage({
      id: 'shifts.fields.qualifications',
    }),
    sortable: true,
    width: 140,
    sortComparator: (v1, v2) => {
      if (!v1) {
        return 0;
      }
      if (!v2) {
        return 1;
      }
      return `${v1[0].abbreviation}`.localeCompare(`${v2[0].abbreviation}`);
    },
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }

      return params.value.map((qual) => (
        <Tooltip key={qual.id} title={qual.name || ''}>
          <Chip variant="filled" label={qual.abbreviation} size="small" sx={{ mr: 1 }} />
        </Tooltip>
      ));
    },
  };
}

function getSpecialtyColumn(intl: IntlShape): GridColDef<RecurrenceListItem, RecurrenceListItem['specialty']> {
  return {
    field: 'specialty',
    flex: 1,
    headerName: intl.formatMessage({
      id: 'shifts.fields.specialty',
    }),
    sortComparator: (v1, v2, param1, param2) => {
      if (!v1) {
        return 0;
      }
      if (!v2) {
        return 1;
      }
      return `${v1.name}`.localeCompare(`${v1.name}`);
    },
    width: 170,
    sortable: true,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      return <Chip variant="filled" label={params.value.name} size="small" />;
    },
  };
}

function getStatusColumn(intl: IntlShape): GridColDef<RecurrenceListItem, ShiftStatus> {
  return {
    field: 'status',
    headerName: intl.formatMessage({ id: 'shifts.fields.status' }),
    width: 120,
    sortable: true,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }

      return <ShiftStatusChip value={params.value} />;
    },
  };
}

function getTagsColumn(intl: IntlShape, theme: Theme): GridColDef<RecurrenceListItem, RecurrenceListItem['tags']> {
  return {
    field: 'tags',
    headerName: intl.formatMessage({ id: 'shifts.listPage.table.fields.tags.heading' }),
    width: 70,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      const isEmpty = !Array.isArray(params.value) || !params.value.length || params.value?.every((tag) => !tag.trim());
      return (
        <TooltipNote
          title={
            <>
              {isEmpty && (
                <Typography variant="body2">
                  <FormattedMessage id="shifts.listPage.table.fields.tags.emptyTag" />
                </Typography>
              )}
              {!!params.value &&
                !!params.value.length &&
                params.value.map((tag, idx) => <Chip key={idx} label={tag.trim()} size="small" />)}
            </>
          }
          popoverTooltipSx={{
            px: isEmpty ? 2 : 1,
            color: isEmpty ? 'text.secondary' : 'inherit',
            maxWidth: '300px',
          }}
          placement="bottom-end"
        >
          {/* Span needed to be able to use Tooltip on disabled element */}
          <span>
            <IconButton
              size="small"
              disabled={isEmpty}
              sx={{ color: isEmpty ? theme.palette.text.secondary : theme.palette.primary.main }}
            >
              <FaIcon name="tags" color="inherit" />
            </IconButton>
          </span>
        </TooltipNote>
      );
    },
  };
}

function getNotesColumn(intl: IntlShape): GridColDef<RecurrenceListItem, RecurrenceListItem['notes']> {
  return {
    field: 'notes',
    headerName: intl.formatMessage({ id: 'shifts.fields.notes' }),
    width: 50,
    renderCell: (params) => {
      const isEmpty = !Array.isArray(params.value) || !params.value.length || params.value?.every((tag) => !tag.trim());
      return (
        <TooltipNote
          title={
            <Typography variant="body2">
              {!isEmpty ? params.value : <FormattedMessage id="shifts.emptyNote" />}
            </Typography>
          }
          popoverTooltipSx={{
            px: 2.5,
            color: isEmpty ? 'text.secondary' : 'inherit',
            maxWidth: '300px',
          }}
          placement="bottom-end"
        >
          {/* Span needed to be able to use Tooltip on disabled element */}
          <span>
            <IconButton size="small" disabled={isEmpty}>
              <FaIcon name="memo-pad" color="inherit" />
            </IconButton>
          </span>
        </TooltipNote>
      );
    },
  };
}

function getShareColumn(intl: IntlShape): GridColDef<RecurrenceListItem , RecurrenceListItem['agenciesForShare']> {
  return {
    field: 'agenciesForShare',
    headerName: intl.formatMessage({ id: 'shifts.fields.agenciesForShare' }),
    width: 50,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', height: '100%' }}>
          <AvatarList
            list={params.value.map((v) => ({
              id: v.id,
              avatar: v.logoURL,
              name: v.name,
            }))}
          />
        </Stack>
      );
    },
  };
}

function getAssignedCountColumn(
  intl: IntlShape,
): GridColDef<RecurrenceListItem, RecurrenceListItem['assignedShiftCount']> {
  return {
    field: 'assignedShiftCount',
    headerName: intl.formatMessage({
      id: 'shifts.fields.assignedCount.label',
    }),
    sortable: true,
    width: 140,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }

      return (
        <Stack direction="column" sx={{ height: '100%' }} justifyContent="center" alignItems="flex-end">
          <Tooltip
            title={intl.formatMessage(
              {
                id: 'shifts.fields.assignedCount.tooltip',
              },
              { assignedShiftCount: params.value, totalShiftCount: params.row.totalShiftCount },
            )}
          >
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {params.value} / {params.row.totalShiftCount}
            </Typography>
          </Tooltip>
        </Stack>
      );
    },
  };
}

function getAssigneeColumn(intl: IntlShape): GridColDef<RecurrenceListItem, RecurrenceListItem['assignees']> {
  return {
    field: 'assignee',
    headerName: intl.formatMessage({ id: 'shifts.fields.assignee' }),
    flex: 1,
    // To prevent assignee column component to collapse
    minWidth: 165,
    sortable: true,
    renderCell: ({ row }) => {
      const shift = row as unknown as ShiftOld & {
        assignees: ShiftOld['assignee'][];
      };
      shift.assignees = !shift.assignees ? [shift.assignee] : shift.assignees;
      return (
          <ShiftAssigneeButton
            shift={{
              ...shift,
              id: shift.id,
              qualifications: shift.qualifications,
              assignees: getUniqueObjects(shift.assignees).map((assignee) => ({
                ...assignee,
                agencyId: (assignee as any).agencyId,
                agencyName: (assignee as any).agencyName,
                firstName: (assignee as any).firstName,
                lastName: (assignee as any).lastName,
                id: (assignee as any).id,
                imageUrl: (assignee as any).avatar,
                isInternal: (assignee as any).isInternal,
                orientedList: (assignee as any).orientedList,
                avatar: (assignee as any).imageUrl,
                name: (assignee as any).fullName,
              })),
            }}
            allowOnlyInternals={true}
            unassignWarningMessage={
              <FormattedMessage
                id="shifts.unassignDialog.recurrenceUnassignDisclaimer"
                values={{ recurrence: <FormattedMessage id={`shifts.recurrence.${row.recurrence}`} /> }}
              />
            }
            disabled={row.assignees.length ? !row.canUnassign : !row.canAssign}
          />
      );
    },
  };
}

function getDepartmentColumn(intl: IntlShape): GridColDef<RecurrenceListItem, string | null> {
  return {
    field: 'department',
    flex: 1,
    headerName: intl.formatMessage({
      id: 'shifts.fields.department',
    }),
    width: 170,
    valueGetter: (_, row) => {
      if ('department' in row) {
        return row.department.name;
      }
      return null;
    },
  };
}
